import { Calendar, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useKalendarium } from '../hooks/useKalendarium';

const PREVIEW_COUNT = 4;

function TimelineRow({ ev, showLineBelow, rowClassName = '', style }) {
    return (
        <div className={`flex gap-3 sm:gap-4 ${rowClassName}`} style={style}>
            <div className="flex w-5 shrink-0 flex-col items-center pt-0.5 sm:pt-1">
                <div
                    className={`relative z-10 box-border h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 rounded-full border-2 transition-shadow duration-300 ${
                        ev.status === 'milestone'
                            ? 'bg-amber-400 border-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.35)]'
                            : ev.status === 'done'
                              ? 'bg-red-600 border-red-600'
                              : ev.status === 'pending' || ev.status === 'next'
                                ? 'bg-white border-red-400 animate-pulse'
                                : ev.status === 'future'
                                  ? 'bg-white border-gray-200'
                                  : 'bg-white border-gray-100'
                    }`}
                />
                {showLineBelow ? (
                    <div
                        className="mt-1.5 w-0.5 flex-none self-center rounded-full bg-gray-100 min-h-6 sm:min-h-7"
                        aria-hidden
                    />
                ) : null}
            </div>
            <div className="min-w-0 flex-1 flex flex-col gap-1 pb-0.5 text-left">
                <div className="text-xs sm:text-[10px] font-bold text-gray-400 uppercase tracking-tight leading-snug sm:leading-normal text-left">
                    {ev.date}
                </div>
                <div
                    className={`text-base sm:text-sm leading-snug transition-colors duration-300 ${
                        ev.status === 'future'
                            ? 'text-gray-300'
                            : ev.status === 'milestone'
                              ? 'text-gray-900 font-extrabold'
                              : 'text-gray-800 font-bold'
                    }`}
                >
                    {ev.event}
                </div>
            </div>
        </div>
    );
}

export default function History() {
    const [fullScheduleOpen, setFullScheduleOpen] = useState(false);
    const { events: timelineEvents, loading, error } = useKalendarium();

    useEffect(() => {
        setFullScheduleOpen(false);
    }, [timelineEvents]);

    const eventsNewestFirst = [...timelineEvents].reverse();

    const hasMoreThanPreview = timelineEvents.length > PREVIEW_COUNT;
    const previewEvents = hasMoreThanPreview
        ? eventsNewestFirst.slice(0, PREVIEW_COUNT)
        : eventsNewestFirst;
    const olderEvents = hasMoreThanPreview ? eventsNewestFirst.slice(PREVIEW_COUNT) : [];

    return (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200">
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2 text-gray-400">
                    <Calendar size={18} className="transition-transform duration-300 hover:scale-110" />
                    <span className="text-xs sm:text-[11px] font-bold uppercase tracking-widest">Kalendarium</span>
                </div>
            </div>

            {error && !loading && (
                <p className="text-base sm:text-sm text-red-600 mb-4">{error}</p>
            )}
            {loading && (
                <div className="space-y-8 animate-pulse">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-6 bg-gray-100 rounded w-3/4" />
                    ))}
                </div>
            )}

            {!loading && !error && timelineEvents.length === 0 && (
                <p className="text-base sm:text-sm text-gray-400">Brak wpisów w kalendarium.</p>
            )}

            {!loading && timelineEvents.length > 0 && (
            <div className="flex flex-col">
                <div className="space-y-3 sm:space-y-4">
                    {previewEvents.map((ev, i) => {
                        const showLineBelow =
                            i < previewEvents.length - 1 ||
                            (fullScheduleOpen && olderEvents.length > 0 && i === previewEvents.length - 1);
                        return (
                            <TimelineRow key={`preview-${ev.date}-${i}`} ev={ev} showLineBelow={showLineBelow} />
                        );
                    })}
                </div>

                {hasMoreThanPreview && (
                    <div
                        className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                            fullScheduleOpen ? 'grid-rows-[1fr] mt-8' : 'grid-rows-[0fr] mt-0'
                        }`}
                    >
                        <div className="min-h-0 overflow-hidden">
                            <div className="space-y-3 sm:space-y-4">
                                {olderEvents.map((ev, i) => (
                                    <TimelineRow
                                        key={`older-${ev.date}-${i}`}
                                        ev={ev}
                                        showLineBelow={i < olderEvents.length - 1}
                                        rowClassName={fullScheduleOpen ? 'history-timeline-expand-row' : ''}
                                        style={fullScheduleOpen ? { animationDelay: `${i * 45}ms` } : undefined}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            )}

            {hasMoreThanPreview && !loading && (
                <button
                    type="button"
                    onClick={() => setFullScheduleOpen((v) => !v)}
                    className="w-full mt-10 flex items-center justify-between text-xs sm:text-[11px] font-bold uppercase tracking-widest p-4 bg-gray-50 rounded-2xl text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors duration-300 group"
                >
                    {fullScheduleOpen ? 'Zwiń historię' : 'Pełna historia'}
                    <ChevronRight
                        size={16}
                        className={`shrink-0 transition-transform duration-300 ease-out group-hover:translate-x-0.5 ${
                            fullScheduleOpen ? 'rotate-90' : 'rotate-0'
                        }`}
                    />
                </button>
            )}
        </div>
    );
}
